// CalendarPage — scheduling calendar for the back-office.
//
// Thin composer: manages view state, fetches crews/assignments/unassigned,
// indexes the data, and hands off to the per-view components. Drag-and-drop is
// pointer-based (@dnd-kit): this page owns the single DndContext + DragOverlay
// and the dispatch hook (useCalendarDnd); the per-view components are pure
// draggable/droppable wrappers.

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { CalendarDays, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { Card } from '../components/ui/card.js';
import { Skeleton } from '../components/ui/skeleton.js';
import { Button } from '../components/ui/button.js';
import { cn } from '../lib/utils.js';
import {
  getCrews,
  getCrewMembers,
  getCalendarAssignments,
  getCalendarUnassigned,
  getCalendarTasks,
  getCalendarWarrantyVisits,
} from '../api/client.js';
import type {
  AssignmentPurpose,
  CalendarAssignmentRow,
  CalendarTaskRow,
  CalendarWarrantyVisitRow,
} from '@gutterinv/shared';
import { ScheduledTaskModal } from '../components/ScheduledTaskModal.js';
import { WarrantyVisitModal } from '../components/WarrantyVisitModal.js';
import {
  indexTasksByPersonDay,
  indexTasksByDate,
  anyTaskUnassigned,
} from '../lib/calendar-tasks.js';
import {
  indexWarrantyByPersonDay,
  indexWarrantyByDate,
  anyWarrantyUnassigned,
} from '../lib/calendar-warranty.js';
import {
  CalendarWeekView,
  NO_PEOPLE_ROW_ID,
  type PersonRow,
} from '../components/CalendarWeekView.js';
import {
  CalendarMonthView,
  MonthDropPeoplePicker,
} from '../components/CalendarMonthView.js';
import { CalendarUnassignedSidebar } from '../components/CalendarUnassignedSidebar.js';
import { CalendarDayDetailModal } from '../components/CalendarDayDetailModal.js';
import { useCalendarDnd } from '../hooks/useCalendarDnd.js';
import {
  DEFAULT_CREWS,
  POLL_INTERVAL_MS,
  getDateWindow,
  iterateDaysInclusive,
  navigateWindow,
  toISO,
  type ViewMode,
} from '../lib/calendar-dates.js';
import { usePollActive } from '../hooks/usePollActive.js';

const VIEW_OPTIONS: { value: ViewMode; label: string }[] = [
  { value: 'month', label: 'Month' },
  { value: '2week', label: '2-Week' },
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Day' },
];

export function CalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDateParam = searchParams.get('date');

  const [viewMode, setViewMode] = useState<ViewMode>('2week');
  const [anchor, setAnchor] = useState<Date>(() => {
    if (initialDateParam) {
      const dt = new Date(initialDateParam + 'T00:00:00');
      if (!Number.isNaN(dt.getTime())) return dt;
    }
    return new Date();
  });

  useEffect(() => {
    if (initialDateParam) {
      setSearchParams({}, { replace: true });
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Month-view crew picker state (shown after an unassigned drop on a month cell).
  const [monthDropPending, setMonthDropPending] = useState<{
    jobId: string;
    dateISO: string;
    purpose: AssignmentPurpose;
    /** Original span minus one. 0 = single-day; > 0 = multi-day reassign. */
    durationDays: number;
  } | null>(null);
  // Day-detail modal (click into a busy day in month view)
  const [dayDetailDate, setDayDetailDate] = useState<string | null>(null);
  // Scheduled-task create/edit modal. null = closed; {task:null} = create;
  // {task:X} = edit. defaultDate pre-fills the date in create mode.
  const [taskModal, setTaskModal] = useState<{
    task: CalendarTaskRow | null;
    defaultDate?: string;
  } | null>(null);
  // Warranty visit edit modal (opened from a calendar pill).
  const [warrantyModal, setWarrantyModal] = useState<CalendarWarrantyVisitRow | null>(null);

  // Pointer-based DnD: one context + dispatch for every calendar drag. An
  // unassigned drop on a MONTH day raises the crew picker (no per-row crew
  // context there); everything else fires its mutation directly.
  const dnd = useCalendarDnd({
    onCrewPicker: (req) => setMonthDropPending(req),
  });

  const window = useMemo(() => getDateWindow(anchor, viewMode), [anchor, viewMode]);

  const fromISO = toISO(window.from);
  const toISO_ = toISO(window.to);

  const pollActive = usePollActive();

  const { data: crews, isLoading: crewsLoading } = useQuery({
    queryKey: ['crews'],
    queryFn: getCrews,
    staleTime: 60_000,
  });

  const { data: assignments, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['calendar', 'assignments', fromISO, toISO_],
    queryFn: () => getCalendarAssignments(fromISO, toISO_),
    refetchInterval: pollActive ? POLL_INTERVAL_MS : false,
  });

  const { data: unassigned, isLoading: unassignedLoading } = useQuery({
    queryKey: ['calendar', 'unassigned'],
    queryFn: getCalendarUnassigned,
    refetchInterval: pollActive ? POLL_INTERVAL_MS : false,
  });

  const { data: tasks } = useQuery({
    queryKey: ['calendar', 'tasks', fromISO, toISO_],
    queryFn: () => getCalendarTasks(fromISO, toISO_),
    refetchInterval: pollActive ? POLL_INTERVAL_MS : false,
  });

  const { data: warranty } = useQuery({
    queryKey: ['calendar', 'warranty', fromISO, toISO_],
    queryFn: () => getCalendarWarrantyVisits(fromISO, toISO_),
    refetchInterval: pollActive ? POLL_INTERVAL_MS : false,
  });

  const { data: crewMembers, isLoading: membersLoading } = useQuery({
    queryKey: ['crew-members'],
    queryFn: getCrewMembers,
    staleTime: 60_000,
  });

  const resolvedCrews = crews ?? DEFAULT_CREWS;
  const resolvedMembers = crewMembers ?? [];
  const resolvedAssignments = assignments ?? [];
  const resolvedInstalls = unassigned?.installs ?? [];
  const resolvedEstimates = unassigned?.estimates ?? [];
  const resolvedTasks = tasks ?? [];
  const resolvedWarranty = warranty ?? [];

  const tasksByPersonDay = useMemo(() => indexTasksByPersonDay(resolvedTasks), [resolvedTasks]);
  const tasksByDate = useMemo(() => indexTasksByDate(resolvedTasks), [resolvedTasks]);
  const warrantyByPersonDay = useMemo(
    () => indexWarrantyByPersonDay(resolvedWarranty),
    [resolvedWarranty],
  );
  const warrantyByDate = useMemo(() => indexWarrantyByDate(resolvedWarranty), [resolvedWarranty]);

  const assignmentIndex = useMemo(() => {
    const idx = new Map<string, CalendarAssignmentRow[]>();
    for (const a of resolvedAssignments) {
      const personIds = a.people.length > 0 ? a.people.map((p) => p.id) : [NO_PEOPLE_ROW_ID];
      for (const day of iterateDaysInclusive(a.assignedDateStart, a.assignedDateEnd)) {
        for (const pid of personIds) {
          const key = `${pid}:${day}`;
          const list = idx.get(key);
          if (list) list.push(a);
          else idx.set(key, [a]);
        }
      }
    }
    return idx;
  }, [resolvedAssignments]);

  const personRows = useMemo<PersonRow[]>(() => {
    const rows: PersonRow[] = resolvedMembers
      .filter((m) => m.active && m.role !== 'admin')
      .map((m) => ({ id: m.id, name: m.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    if (
      resolvedAssignments.some((a) => a.people.length === 0) ||
      anyTaskUnassigned(resolvedTasks) ||
      anyWarrantyUnassigned(resolvedWarranty)
    ) {
      rows.push({ id: NO_PEOPLE_ROW_ID, name: '(no people yet)' });
    }
    return rows;
  }, [resolvedMembers, resolvedAssignments, resolvedTasks, resolvedWarranty]);

  const assignmentsByDate = useMemo(() => {
    const idx = new Map<string, CalendarAssignmentRow[]>();
    for (const a of resolvedAssignments) {
      for (const day of iterateDaysInclusive(a.assignedDateStart, a.assignedDateEnd)) {
        const list = idx.get(day);
        if (list) list.push(a);
        else idx.set(day, [a]);
      }
    }
    return idx;
  }, [resolvedAssignments]);

  const crewColorMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of resolvedCrews) {
      m.set(c.id, c.color);
    }
    return m;
  }, [resolvedCrews]);

  const handlePrev = useCallback(
    () => setAnchor((a) => navigateWindow(a, viewMode, -1)),
    [viewMode],
  );
  const handleNext = useCallback(
    () => setAnchor((a) => navigateWindow(a, viewMode, 1)),
    [viewMode],
  );
  const handleToday = useCallback(() => setAnchor(new Date()), []);

  const isLoading = crewsLoading || assignmentsLoading || membersLoading;

  return (
    <div className="pt-6 flex gap-6 h-full min-h-0">
      <DndContext
        sensors={dnd.sensors}
        collisionDetection={dnd.collisionDetection}
        onDragStart={dnd.onDragStart}
        onDragEnd={dnd.onDragEnd}
        onDragCancel={dnd.onDragCancel}
      >
        <div className="flex-1 min-w-0 space-y-4">
          <CalendarHeader
            viewMode={viewMode}
            onViewChange={setViewMode}
            dateLabel={window.label}
            onPrev={handlePrev}
            onNext={handleNext}
            onToday={handleToday}
            onNewTask={() => setTaskModal({ task: null })}
          />

          {isLoading ? (
            <CalendarSkeleton />
          ) : viewMode === 'month' ? (
            <CalendarMonthView
              days={window.days}
              anchor={anchor}
              assignmentsByDate={assignmentsByDate}
              tasksByDate={tasksByDate}
              onTaskClick={(t) => setTaskModal({ task: t })}
              warrantyByDate={warrantyByDate}
              onWarrantyClick={(v) => setWarrantyModal(v)}
              crewColorMap={crewColorMap}
              crews={resolvedCrews}
              onDayClick={setDayDetailDate}
            />
          ) : viewMode === '2week' ? (
            <div className="space-y-4">
              <CalendarWeekView
                days={window.days.slice(0, 7)}
                people={personRows}
                assignmentIndex={assignmentIndex}
                tasksByPersonDay={tasksByPersonDay}
                onTaskClick={(t) => setTaskModal({ task: t })}
                warrantyByPersonDay={warrantyByPersonDay}
                onWarrantyClick={(v) => setWarrantyModal(v)}
              />
              <CalendarWeekView
                days={window.days.slice(7, 14)}
                people={personRows}
                assignmentIndex={assignmentIndex}
                tasksByPersonDay={tasksByPersonDay}
                onTaskClick={(t) => setTaskModal({ task: t })}
                warrantyByPersonDay={warrantyByPersonDay}
                onWarrantyClick={(v) => setWarrantyModal(v)}
              />
            </div>
          ) : (
            <CalendarWeekView
              days={window.days}
              people={personRows}
              assignmentIndex={assignmentIndex}
              tasksByPersonDay={tasksByPersonDay}
              onTaskClick={(t) => setTaskModal({ task: t })}
              warrantyByPersonDay={warrantyByPersonDay}
              onWarrantyClick={(v) => setWarrantyModal(v)}
            />
          )}

          {/* Crew legend — month view only (week/2-week show crew names in row headers) */}
          {viewMode === 'month' && (
            <div className="flex items-center gap-4 px-1">
              {resolvedCrews.map((c) => (
                <div key={c.id} className="flex items-center gap-1.5">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: c.color }}
                  />
                  <span className="text-xs text-muted-foreground">{c.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <CalendarUnassignedSidebar
          installs={resolvedInstalls}
          estimates={resolvedEstimates}
          isLoading={unassignedLoading}
        />

        {/* Floating preview of the dragged item (replaces the old setDragImage hack). */}
        <DragOverlay dropAnimation={null}>
          {dnd.activeDrag ? (
            <div className="max-w-[16rem] truncate rounded-md border border-primary/40 bg-card px-2 py-1 text-xs font-medium text-foreground shadow-lg cursor-grabbing">
              {dnd.activeDrag.overlayLabel}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {monthDropPending && (
        <MonthDropPeoplePicker
          jobId={monthDropPending.jobId}
          dateISO={monthDropPending.dateISO}
          purpose={monthDropPending.purpose}
          durationDays={monthDropPending.durationDays}
          onClose={() => setMonthDropPending(null)}
        />
      )}

      {dayDetailDate && (
        <CalendarDayDetailModal
          dateISO={dayDetailDate}
          assignments={assignmentsByDate.get(dayDetailDate) ?? []}
          tasks={tasksByDate.get(dayDetailDate) ?? []}
          warranty={warrantyByDate.get(dayDetailDate) ?? []}
          onClose={() => setDayDetailDate(null)}
        />
      )}

      {taskModal && (
        <ScheduledTaskModal
          task={taskModal.task}
          defaultDate={taskModal.defaultDate}
          onClose={() => setTaskModal(null)}
        />
      )}

      {warrantyModal && (
        <WarrantyVisitModal
          jobId={warrantyModal.jobId}
          visit={warrantyModal}
          jobLabel={warrantyModal.customerName}
          onClose={() => setWarrantyModal(null)}
        />
      )}
    </div>
  );
}

function CalendarHeader({
  viewMode,
  onViewChange,
  dateLabel,
  onPrev,
  onNext,
  onToday,
  onNewTask,
}: {
  viewMode: ViewMode;
  onViewChange: (v: ViewMode) => void;
  dateLabel: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onNewTask: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3">
        <CalendarDays className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-xl font-semibold text-foreground">Calendar</h1>

        <div className="ml-4 flex rounded-lg border border-border bg-muted/50 p-0.5">
          {VIEW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onViewChange(opt.value)}
              className={cn(
                'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                viewMode === opt.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={onNewTask}>
          <Plus className="mr-1 h-4 w-4" />
          New Task
        </Button>
        <Button variant="outline" size="sm" onClick={onToday}>
          Today
        </Button>
        <Button variant="ghost" size="sm" onClick={onPrev} className="h-8 w-8 p-0">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-[180px] text-center text-sm font-medium text-foreground">
          {dateLabel}
        </span>
        <Button variant="ghost" size="sm" onClick={onNext} className="h-8 w-8 p-0">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function CalendarSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="space-y-0">
        <div className="flex border-b border-border">
          <Skeleton className="m-2 h-8 w-28" />
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="m-2 h-8 flex-1" />
          ))}
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex border-b border-border last:border-b-0">
            <Skeleton className="m-2 h-16 w-28" />
            {Array.from({ length: 7 }).map((_, j) => (
              <Skeleton key={j} className="m-2 h-16 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
}
